import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Clock, Loader2, Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/utils";
import { listPublishedLibraryModules } from "@/modules/learningCenter/features/ModuleLibrary/api/listPublishedLibraryModules";
import {
  LibraryModuleListItem,
  LIBRARY_MODULE_DIFFICULTY_OPTIONS,
} from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

const ALL_LEVELS = "all";

export interface SelectedLibraryModule {
  uuid: string;
  title: string;
}

interface AddFromLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (modules: SelectedLibraryModule[]) => void;
}

/**
 * Modal for browsing and selecting a published library module to add to a
 * course. Mirrors the labs `ModuleLibraryBrowser` layout: title + subtitle,
 * search, a Level filter, a selectable results list with an empty state, and a
 * Cancel / Add Module footer.
 *
 * Selecting a module and confirming hands its uuid/title back so the course
 * editor can add it as a new module (the server copies its content as an
 * independent snapshot).
 */
export function AddFromLibraryModal({
  isOpen,
  onClose,
  onConfirm,
}: AddFromLibraryModalProps) {
  const [modules, setModules] = useState<LibraryModuleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>(ALL_LEVELS);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSearchTerm("");
    setLevelFilter(ALL_LEVELS);
    setSelectedUuid(null);
  }, []);

  // Load published modules (server-filtered by level) whenever the modal is
  // open or the level filter changes. Search is filtered client-side.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    listPublishedLibraryModules(
      levelFilter === ALL_LEVELS ? {} : { difficulty: levelFilter },
    )
      .then((response) => {
        if (!controller.signal.aborted) {
          setModules(response.data);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [isOpen, levelFilter]);

  const filteredModules = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return modules;
    }
    return modules.filter(
      (module) =>
        module.title.toLowerCase().includes(term) ||
        (module.description?.toLowerCase().includes(term) ?? false),
    );
  }, [modules, searchTerm]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleConfirm = useCallback(() => {
    const selected = modules.find((module) => module.uuid === selectedUuid);
    if (!selected) {
      return;
    }
    onConfirm([{ uuid: selected.uuid, title: selected.title }]);
    resetState();
    onClose();
  }, [modules, selectedUuid, onConfirm, onClose, resetState]);

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      open={isOpen}
    >
      <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Add Module from Library</DialogTitle>
          <DialogDescription>
            Browse and select a module from your library to add to this course.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-col gap-3 py-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <Input
              aria-label="Search modules"
              className="pl-10"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search modules..."
              value={searchTerm}
            />
          </div>
          <Select onValueChange={setLevelFilter} value={levelFilter}>
            <SelectTrigger
              aria-label="Filter by difficulty"
              className="sm:w-[180px]"
            >
              <SelectValue placeholder="All difficulties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_LEVELS}>All difficulties</SelectItem>
              {LIBRARY_MODULE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <SelectItem key={difficulty} value={difficulty}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        <div className="min-h-[280px] flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex h-[280px] items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : filteredModules.length === 0 ? (
            <div className="flex h-[280px] flex-col items-center justify-center text-center">
              <BookOpen className="mb-3 h-12 w-12 text-gray-300 dark:text-slate-600" />
              <p className="text-base font-medium text-gray-900 dark:text-white">
                No modules found
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {modules.length === 0
                  ? "No published modules in your library yet"
                  : "Try adjusting your search or filters"}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {filteredModules.map((module) => {
                const isSelected = module.uuid === selectedUuid;
                return (
                  <li key={module.uuid}>
                    <button
                      aria-pressed={isSelected}
                      className={cn(
                        "w-full rounded-lg border px-4 py-3 text-left transition-colors",
                        isSelected
                          ? "border-primary bg-primary/5 dark:bg-primary/10"
                          : "border-gray-200 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700/50",
                      )}
                      onClick={() => setSelectedUuid(module.uuid)}
                      type="button"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                            {module.title}
                          </span>
                          {module.description && (
                            <span className="mt-0.5 line-clamp-2 block text-xs text-gray-500 dark:text-gray-400">
                              {module.description}
                            </span>
                          )}
                          <span className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            {module.difficulty && (
                              <Badge variant="grey">
                                {module.difficulty.charAt(0).toUpperCase() +
                                  module.difficulty.slice(1)}
                              </Badge>
                            )}
                            {module.estimatedMinutes != null && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {module.estimatedMinutes} min
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                              <BookOpen className="h-3 w-3" />
                              {module.blockCount}{" "}
                              {module.blockCount === 1 ? "block" : "blocks"}
                            </span>
                          </span>
                        </span>
                        {isSelected && (
                          <Badge className="shrink-0">Selected</Badge>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={!selectedUuid}
            onClick={handleConfirm}
            type="button"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add Module
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
